// frontend/src/client.ts
import { v4 as uuid } from 'uuid'

type Options = RequestInit & { traceId?: string }

/* ------------------------ traceparent ------------------------ */
export function traceparent(id?: string) {
  const traceId = (id || uuid()).replace(/-/g, '')
  const parentId = traceId.slice(0, 16)
  return `00-${traceId}-${parentId}-01`
}

/* ---------------- Safe base + URL join (no new URL) ---------- */
const RAW_BASE = (import.meta as any)?.env?.VITE_API_BASE as string | undefined

function computeBase(): string {
  if (RAW_BASE && /^https?:\/\//i.test(RAW_BASE)) return RAW_BASE.replace(/\/$/, '')
  if (RAW_BASE && RAW_BASE.trim().startsWith('/')) return RAW_BASE.trim().replace(/\/$/, '')
  return '/v1'
}
const API_BASE = computeBase()

function buildUrl(path: string): string {
  if (/^https?:\/\//i.test(path)) return path
  const p = path.startsWith('/') ? path : `/${path}`
  return `${API_BASE}${p}`
}

/* ----------------------- CSRF helpers ------------------------ */
const SAFE_METHODS = new Set(['GET', 'HEAD', 'OPTIONS', 'TRACE'])

function getCookie(name: string) {
  const m = document.cookie.match(new RegExp('(?:^|;\\s*)' + name + '=([^;]*)'))
  return m ? decodeURIComponent(m[1]) : undefined
}

async function ensureCsrf() {
  // If no csrftoken cookie, fetch one (double-submit pattern).
  if (!getCookie('csrftoken')) {
    // Use same-origin path so Vite proxy or NGINX reverse-proxy carries cookies.
    await fetch('/v1/csrf', { credentials: 'include' }).catch(() => {})
  }
}

/* ---------------- RAW fetch: fresh Response every call ------- */
export async function api(path: string, opts: Options = {}) {
  const url = buildUrl(path)
  const method = (opts.method || 'GET').toUpperCase()
  const headers = new Headers(opts.headers || {})

  // always set tracing / demo-tenant headers
  headers.set('traceparent', traceparent(opts.traceId))
  headers.set('X-Tenant-ID', 'root') // RLS demo; replace when OIDC is wired

  // For unsafe methods, ensure CSRF token and include it; keep cookies flowing.
  if (!SAFE_METHODS.has(method)) {
    await ensureCsrf()
    const token = getCookie('csrftoken')
    if (token) headers.set('X-CSRF-Token', token)
    if (opts.credentials === undefined) opts.credentials = 'include'
  }

  return fetch(url, { ...opts, method, headers })
}

/* --------------- JSON helper: parsed-value cache ------------- */
const inflightJson = new Map<string, Promise<any>>()

export async function apiJson<T = any>(path: string, opts: Options = {}, cache = true): Promise<T> {
  const url = buildUrl(path)
  const key = JSON.stringify([url, (opts.method || 'GET').toUpperCase(), opts.body || ''])

  if (cache && inflightJson.has(key)) return inflightJson.get(key)!

  const p = (async () => {
    const res = await api(path, opts)

    if (!res.ok) {
      const text = await res.text().catch(() => '')
      throw new Error(`HTTP ${res.status}: ${text || res.statusText}`)
    }

    const ct = res.headers.get('content-type') || ''
    if (!ct.includes('application/json')) {
      const text = await res.text().catch(() => '')
      throw new Error(`Expected JSON but got '${ct || 'unknown'}' with body: ${text.slice(0, 200)}`)
    }

    return res.json() as Promise<T>
  })()

  if (cache) inflightJson.set(key, p)
  return p
}

/* --------------------------- SSE ----------------------------- */
export const sse = (path: string) => {
  const url = buildUrl(path)
  return new EventSource(url, { withCredentials: false })
}

/* -------------------------- Debug ---------------------------- */
// eslint-disable-next-line no-console
console.log('[client.ts] API_BASE =', API_BASE, 'RAW =', RAW_BASE)